import { LocalCliError } from "./errors.js";

export interface AppManifest {
  name: string;
  url: string;
  redirect_url: string;
  /** Where GitHub redirects the user AFTER they install the App (post-install
   *  setup). This is what brings the founder back from GitHub's install-confirm
   *  page to our localhost "Installed — return to your terminal" page. */
  setup_url: string;
  public: boolean;
  default_permissions: Record<string, string>;
  default_events: string[];
}

/** The per-founder brain App: minimal permissions, no webhook (omitting
 *  hook_attributes avoids GitHub's "url wasn't supplied" manifest rejection).
 *
 *  `nameSuffix` is a short unique token (e.g. 6 hex chars) appended to the App
 *  name. GitHub App names are globally unique AND a deleted App's name is NOT
 *  freed (held with no documented TTL), so a fixed `m8t-brain-<org>` name blocks
 *  re-creation after a delete and a second environment in the same org. The
 *  suffix sidesteps both. Nothing re-derives the App from this name — the local
 *  creds file records the appId/slug — so a per-create suffix is safe. */
export function buildManifest(args: { org: string; redirectUrl: string; setupUrl: string; nameSuffix: string }): AppManifest {
  return {
    name: `m8t-brain-${args.org}-${args.nameSuffix}`,
    url: `https://github.com/${args.org}`,
    redirect_url: args.redirectUrl,
    setup_url: args.setupUrl,
    public: false,
    // workflows:write is REQUIRED — the brain template ships .github/workflows/*
    // (skill-gate.yml, librarian.yml); GitHub rejects an App push of workflow files
    // without it. (The gh fallback works because the user token carries the workflow scope.)
    default_permissions: { contents: "write", administration: "write", workflows: "write", metadata: "read" },
    default_events: [],
  };
}

export function newAppUrl(org: string, state: string): string {
  return `https://github.com/organizations/${org}/settings/apps/new?state=${state}`;
}

export interface ConvertedApp {
  id: string;
  slug: string;
  name: string;
  pem: string;
  htmlUrl: string;
}

/** Exchange the manifest `code` for the App's credentials (within 1h of creation). */
export async function convertManifestCode(code: string): Promise<ConvertedApp> {
  const res = await fetch(`https://api.github.com/app-manifests/${code}/conversions`, {
    method: "POST",
    headers: { Accept: "application/vnd.github+json", "X-GitHub-Api-Version": "2022-11-28", "User-Agent": "m8t" },
  });
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({ code: "GH_APP_MANIFEST_CONVERT_FAILED", message: `manifest conversion HTTP ${res.status.toString()}: ${text.slice(0, 400)}` });
  }
  const j = JSON.parse(text) as { id: number; slug: string; name: string; pem: string; html_url: string };
  return { id: String(j.id), slug: j.slug, name: j.name, pem: j.pem, htmlUrl: j.html_url };
}
